/**
 * Returns formatted date string
 */
export function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * Returns month and year for header display
 */
export function formatMonthYear(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });
}

/**
 * Normalize date to local midnight to prevent timezone issues
 * This ensures all date comparisons use the same reference point
 */
export function normalizeDate(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

/**
 * Get date string in YYYY-MM-DD format for consistent comparisons
 */
export function getDateString(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Get day of week abbreviated (MON, TUE, etc.)
 */
export function getDayOfWeekShort(date: Date): string {
  const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  return days[date.getDay()];
}

/**
 * Get day of month as two digits (01, 02, etc.)
 */
export function getDayOfMonth(date: Date): string {
  const day = date.getDate();
  return day < 10 ? `0${day}` : `${day}`;
}

/**
 * Gets array of dates for the week
 */
export function getWeekDates(date: Date = new Date()): Date[] {
  // Create a normalized copy to avoid any reference issues and ensure consistent dates
  date = normalizeDate(new Date(date.getTime()));
  const currentDay = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const diff = date.getDate() - currentDay + (currentDay === 0 ? -6 : 1); // Adjust when day is Sunday
  const monday = new Date(date.setDate(diff));

  const weekDates: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const nextDay = new Date(monday);
    nextDay.setDate(monday.getDate() + i);
    // Make sure each date is normalized to midnight
    weekDates.push(normalizeDate(nextDay));
  }

  return weekDates;
}

/**
 * Get week range for display (e.g., "April 1-7, 2025")
 */
export function getWeekRange(date: Date = new Date()): string {
  const weekDates = getWeekDates(date);
  const startDate = weekDates[0];
  const endDate = weekDates[6];

  const sameMonth = startDate.getMonth() === endDate.getMonth();

  if (sameMonth) {
    return `${startDate.toLocaleDateString('en-US', { month: 'long' })} ${startDate.getDate()}-${endDate.getDate()}, ${startDate.getFullYear()}`;
  } else {
    return `${startDate.toLocaleDateString('en-US', { month: 'long' })} ${startDate.getDate()} - ${endDate.toLocaleDateString('en-US', { month: 'long' })} ${endDate.getDate()}, ${startDate.getFullYear()}`;
  }
}

/**
 * Get week number in year (1-52)
 */
export function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

/**
 * Get ISO week number and year
 */
export function getISOWeek(date: Date): { weekNumber: number, year: number } {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);

  return {
    weekNumber: weekNo,
    year: d.getUTCFullYear()
  };
}

/**
 * Check if two dates are the same day
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return getDateString(date1) === getDateString(date2);
}

/**
 * Check if date is today
 */
export function isToday(date: Date): boolean {
  return isSameDay(date, normalizeDate(new Date()));
}
